import { useEffect, useState } from "react";
import { Link, useParams, Navigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, LifeBuoy, ArrowRight, ShieldCheck, AlertTriangle, Mail } from "lucide-react";
import useNoIndex from "@/hooks/useNoIndex";
import useSEO from "@/hooks/useSEO";
import { track, useTrackPageView } from "@/lib/analytics";
import BookCover from "@/components/books/BookCover";
import Reveal from "@/components/books/Reveal";
import { HeroFade } from "@/components/books/KineticTitle";
import { CYBER_BOOKS, CYBER_BUNDLE, CYBER_SAFETY_SUPPORT, CYBER_SAFETY_ACCENT as C, findCyberProduct } from "@/data/cyberSafetyBooks";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hidden post-purchase download page — /books/cyber-safety/order/<slug>
 * (slug = a book slug or "complete-series"). Reuses the EXACT same
 * generic verification engine as the Chinnu OrderDownload page
 * (GET /api/books/verify-token, POST /api/books/verify-return,
 * GET /api/books/download/{slug}) — only the copy/branding differs.
 * A paid PDF is NEVER served without a verified payment.
 */
export default function CyberSafetyOrderDownload() {
  useNoIndex();
  useTrackPageView("cyber_payment_success", { channel: "razorpay" });

  const { slug } = useParams();
  const [params] = useSearchParams();
  const product = findCyberProduct(slug);
  const isBundle = slug === CYBER_BUNDLE.slug;

  const [verify, setVerify] = useState({ state: "checking", token: null });

  useSEO({
    title: "Thank You — LifeIQ Cyber Safety",
    description: "Your LifeIQ Cyber Safety eBook is ready. Download your digital edition.",
    canonicalPath: `/books/cyber-safety/order/${slug}`,
  });

  useEffect(() => {
    if (!product) return;
    const q = Object.fromEntries(params.entries());
    const directToken = params.get("token");
    const reference = q.razorpay_payment_id || params.get("ref") || null;
    track("cyber_payment_return", {
      product_id: product.product_id,
      offer_price: product.price_value,
      regular_price: product.regular_price_value,
      has_razorpay_params: Boolean(q.razorpay_payment_id || directToken),
      reference,
    });

    if (directToken) {
      fetch(`${API}/api/books/verify-token?slug=${encodeURIComponent(slug)}&token=${encodeURIComponent(directToken)}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.verified) setVerify({ state: "verified", token: directToken });
          else setVerify({ state: "locked", token: null, reason: "invalid_token" });
        })
        .catch(() => setVerify({ state: "locked", token: null, reason: "network_error" }));
      return;
    }

    fetch(`${API}/api/books/verify-return`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug,
        razorpay_payment_id: q.razorpay_payment_id || null,
        razorpay_payment_link_id: q.razorpay_payment_link_id || null,
        razorpay_payment_link_reference_id: q.razorpay_payment_link_reference_id || null,
        razorpay_payment_link_status: q.razorpay_payment_link_status || null,
        razorpay_signature: q.razorpay_signature || null,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.verified && data.token) setVerify({ state: "verified", token: data.token });
        else setVerify({ state: "locked", token: null, reason: data.verification || "unverified" });
      })
      .catch(() => setVerify({ state: "locked", token: null, reason: "network_error" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!product) return <Navigate to="/books/cyber-safety" replace />;

  const canDownload = verify.state === "verified";
  const checking = verify.state === "checking";
  const locked = verify.state === "locked";
  const reference = params.get("razorpay_payment_id") || params.get("ref") || "—";

  const dlUrl = (downloadSlug) => {
    const base = `/api/books/download/${downloadSlug}`;
    return verify.token ? `${base}?token=${encodeURIComponent(verify.token)}` : base;
  };

  const download = (downloadSlug, productId, isZip = false) => {
    if (!canDownload) return;
    track(isZip ? "cyber_bundle_download" : "cyber_download", {
      product_id: productId,
      reference,
      verification: verify.state,
    });
    window.location.assign(dlUrl(downloadSlug));
  };

  const displayTitle = isBundle ? CYBER_BUNDLE.title : product.title;

  return (
    <main>
      <section className="min-h-[70vh] relative overflow-hidden" style={{ backgroundColor: C.cream }}>
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-20 md:py-28 relative">
          <HeroFade delay={0.05}>
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-full text-white mx-auto flex items-center justify-center"
                style={{ backgroundColor: locked ? C.red : C.teal }}
              >
                {locked ? <AlertTriangle size={26} /> : <CheckCircle2 size={28} />}
              </div>
              <div className="mt-6 text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>
                {checking ? "Confirming Your Payment" : locked ? "Verification Needed" : "Payment Successful"}
              </div>
              <h1 data-testid="cyber-order-download-title" className="mt-4 font-serif-d text-[30px] sm:text-[44px] leading-[1.1]" style={{ color: C.navy }}>
                {checking && "Confirming your payment…"}
                {canDownload && (isBundle ? "Your complete Cyber Fraud & Scam Safety Series is ready." : "Your LifeIQ Cyber Safety eBook is ready.")}
                {locked && "We couldn't verify this purchase"}
              </h1>
            </div>
          </HeroFade>

          {/* RECEIPT + DOWNLOAD (only rendered once payment is verified) */}
          {canDownload && (
            <Reveal delay={0.1}>
              <div data-testid="cyber-order-receipt-summary" className="mt-12 bg-white border rounded-sm p-6 sm:p-8" style={{ borderColor: `${C.navy}14` }}>
                {isBundle ? (
                  <div>
                    <div className="grid grid-cols-3 gap-3 max-w-[420px] mx-auto">
                      {CYBER_BOOKS.map((b) => <BookCover key={b.slug} book={b} testId={`cyber-order-cover-${b.slug}`} />)}
                    </div>
                    <div className="mt-6 text-center">
                      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: C.textMuted }}>Digital Collection · 3 PDFs</div>
                      <h2 className="mt-2 font-serif-d text-[22px] sm:text-[26px] leading-[1.15]" style={{ color: C.navy }}>{displayTitle}</h2>
                      <div data-testid="cyber-order-format-attribute" className="mt-2 text-[12px] uppercase tracking-[0.14em] font-bold" style={{ color: C.teal }}>
                        Digital Edition · PDF · English
                      </div>
                    </div>

                    <p className="mt-6 text-[14px] text-center" style={{ color: C.textMuted }}>Your digital books are ready. Tap any button below to download.</p>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {CYBER_BOOKS.map((b) => (
                        <button
                          key={b.slug}
                          type="button"
                          data-testid={`cyber-download-book-${b.slug}`}
                          onClick={() => download(b.slug, b.product_id)}
                          className="inline-flex items-center justify-center gap-2 border px-5 py-3.5 text-[12px] font-semibold tracking-wide uppercase rounded-sm hover:text-white transition-colors duration-200"
                          style={{ borderColor: C.navy, color: C.navy }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.navy; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        >
                          <Download size={14} /> Download Book {b.book_number} — {b.title}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      data-testid="cyber-download-zip-button"
                      onClick={() => download(CYBER_BUNDLE.slug, CYBER_BUNDLE.product_id, true)}
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:opacity-90 transition-opacity duration-200 rounded-sm"
                      style={{ backgroundColor: C.gold, color: C.navy }}
                    >
                      <Download size={15} /> Download All 3 Books — ZIP
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    <div className="md:col-span-4 max-w-[220px] mx-auto w-full">
                      <BookCover book={product} testId="cyber-order-book-cover" />
                    </div>
                    <div className="md:col-span-8">
                      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: C.textMuted }}>
                        {product.label} · Digital Edition · PDF
                      </div>
                      <h2 className="mt-2 font-serif-d text-[22px] sm:text-[26px] leading-[1.15]" style={{ color: C.navy }}>{displayTitle}</h2>
                      <div data-testid="cyber-order-format-attribute" className="mt-2 text-[12px] uppercase tracking-[0.14em] font-bold" style={{ color: C.teal }}>
                        Digital Edition · PDF
                      </div>

                      <p className="mt-5 text-[14px] leading-[1.6]" style={{ color: C.textMuted }}>
                        Your digital book is ready. Tap the button below to download your PDF.
                      </p>
                      <button
                        type="button"
                        data-testid="cyber-download-pdf-button"
                        onClick={() => download(product.slug, product.product_id)}
                        className="mt-4 inline-flex items-center gap-2 text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:opacity-90 transition-opacity duration-200 rounded-sm"
                        style={{ backgroundColor: C.gold, color: C.navy }}
                      >
                        <Download size={15} /> Download Your Book
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-7 pt-5 border-t" style={{ borderColor: `${C.navy}12` }}>
                  <div data-testid="cyber-verification-badge" className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: C.teal }}>
                    <ShieldCheck size={15} /> Payment verified with Razorpay
                  </div>
                  <div className="mt-3 text-[13px]" style={{ color: C.textMuted }}>
                    <span className="uppercase tracking-[0.18em] font-semibold">Order reference</span>
                    <span data-testid="cyber-order-reference" className="ml-3 font-mono text-[14px]" style={{ color: C.navy }}>{reference}</span>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {checking && (
            <Reveal delay={0.1}>
              <div data-testid="cyber-verification-checking" className="mt-12 bg-white border rounded-sm p-8 sm:p-10 text-center" style={{ borderColor: `${C.navy}14` }}>
                <div className="text-[14px]" style={{ color: C.textMuted }}>Confirming your payment with Razorpay…</div>
              </div>
            </Reveal>
          )}

          {locked && (
            <Reveal delay={0.1}>
              <div data-testid="cyber-verification-locked" className="mt-12 bg-white border rounded-sm p-8 sm:p-10" style={{ borderColor: `${C.red}44` }}>
                <div className="flex items-start gap-4">
                  <AlertTriangle size={22} className="shrink-0 mt-0.5" style={{ color: C.red }} />
                  <div>
                    <p data-testid="cyber-verification-locked-message" className="text-[16px] sm:text-[17px] leading-[1.6]" style={{ color: C.navy }}>
                      We couldn't verify this purchase. If you have already paid, please contact LifeIQ support with your payment details.
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[14px]" style={{ color: C.navy }}>
                      <a data-testid="cyber-locked-support-email" href={`mailto:${CYBER_SAFETY_SUPPORT.email}`} className="inline-flex items-center gap-2 underline underline-offset-2 transition-colors" style={{ textDecorationColor: C.teal }}>
                        <Mail size={14} /> {CYBER_SAFETY_SUPPORT.email}
                      </a>
                      <a data-testid="cyber-locked-support-whatsapp" href={CYBER_SAFETY_SUPPORT.whatsapp_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 underline underline-offset-2 transition-colors">
                        WhatsApp {CYBER_SAFETY_SUPPORT.whatsapp}
                      </a>
                    </div>
                    <div className="mt-5 pt-4 border-t text-[13px]" style={{ borderColor: `${C.navy}12`, color: C.textMuted }}>
                      <span className="uppercase tracking-[0.18em] font-semibold">Order reference</span>
                      <span data-testid="cyber-order-reference" className="ml-3 font-mono text-[14px]" style={{ color: C.navy }}>{reference}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {/* SUPPORT */}
          <Reveal delay={0.2}>
            <div className="mt-10 rounded-sm border bg-white p-6 sm:p-7" style={{ borderColor: `${C.navy}14` }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full text-white flex items-center justify-center shrink-0" style={{ backgroundColor: C.teal }}><LifeBuoy size={18} /></div>
                <div>
                  <div className="font-serif-d text-[18px]" style={{ color: C.navy }}>Need help with your purchase or download?</div>
                  <p className="mt-1 text-[14px] leading-[1.65]" style={{ color: C.textMuted }}>
                    If the download doesn't start, refresh this page or write to us with your order reference. We reply within one working day.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px]" style={{ color: C.navy }}>
                    <a data-testid="cyber-support-email" href={`mailto:${CYBER_SAFETY_SUPPORT.email}`} className="inline-flex items-center gap-2 underline underline-offset-2 transition-colors">
                      <Mail size={14} /> {CYBER_SAFETY_SUPPORT.email}
                    </a>
                    <a data-testid="cyber-support-whatsapp" href={CYBER_SAFETY_SUPPORT.whatsapp_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 underline underline-offset-2 transition-colors">
                      WhatsApp {CYBER_SAFETY_SUPPORT.whatsapp}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* CONTINUE EXPLORING */}
          <Reveal delay={0.25}>
            <div className="mt-14">
              <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.gold }}>Continue exploring the series</div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {CYBER_BOOKS.filter((b) => b.slug !== slug).slice(0, 3).map((b) => (
                  <Link
                    key={b.slug}
                    to={`/books/cyber-safety/${b.slug}`}
                    data-testid={`cyber-order-explore-${b.slug}`}
                    className="bg-white border rounded-sm p-4 flex items-center gap-3 hover:shadow-md transition-all duration-200"
                    style={{ borderColor: `${C.navy}14` }}
                  >
                    <div className="w-12 shrink-0"><BookCover book={b} /></div>
                    <div className="text-[14px] font-serif-d leading-tight" style={{ color: C.navy }}>
                      {b.title}
                      <div className="text-[11px] uppercase tracking-[0.16em] font-semibold mt-1 flex items-center gap-1" style={{ color: C.teal }}>Explore <ArrowRight size={11} /></div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
